import { computed, ref, watch } from "vue";
import { useAuth } from "~/composables/useAuth";

export interface ArtistAccessInfo {
  id: string;
  name: string | null;
  slug: string | null;
  photo_name: string | null;
}

// Share promise to deduplicate concurrent fetches across components
let fetchPromise: Promise<void> | null = null;
let lastCheckedKey: string | null = null;

export const useArtistAccess = () => {
  const { isLoggedIn, user } = useAuth();
  const supabase = useSupabaseClient();

  // Use useState to share state securely and avoid SSR cross-request pollution
  const isArtistState = useState<boolean>('isArtistState', () => false);
  const artistState = useState<ArtistAccessInfo | null>('artistState', () => null);
  const isLoadingState = useState<boolean>('isLoadingArtistState', () => false);

  async function checkAccess() {
    const currentKey = `${user.value?.id || 'none'}-${isLoggedIn.value}`;
    if (currentKey === lastCheckedKey) return;

    if (!isLoggedIn.value) {
      lastCheckedKey = currentKey;
      isArtistState.value = false;
      artistState.value = null;
      return;
    }

    // Deduplicate concurrent requests
    if (fetchPromise) {
      await fetchPromise;
      return;
    }

    isLoadingState.value = true;
    lastCheckedKey = currentKey;

    fetchPromise = (async () => {
      try {
        // Pass the session token in the header to avoid race conditions 
        // immediately after login where the cookie isn't set yet.
        const session = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session.data.session?.access_token) {
          headers.Authorization = `Bearer ${session.data.session.access_token}`;
        }

        const res = await $fetch<{ isArtist: boolean; artist: ArtistAccessInfo | null }>(
          "/api/artist-dashboard/access",
          { headers }
        );

        if (!isLoggedIn.value) return;

        isArtistState.value = res.isArtist;
        artistState.value = res.artist;
      } catch (err) {
        console.error("[useArtistAccess] Failed to check artist access:", err);
        isArtistState.value = false;
        artistState.value = null;
      } finally {
        isLoadingState.value = false;
        fetchPromise = null;
      }
    })();

    await fetchPromise;
  }

  watch([() => user.value?.id, () => isLoggedIn.value], checkAccess, { immediate: true });

  return {
    isArtist: computed(() => isArtistState.value),
    artist: computed(() => artistState.value),
    isLoading: computed(() => isLoadingState.value),
  };
};
